import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  collection, getDocs, addDoc, setDoc,
  serverTimestamp, deleteDoc, doc
} from 'firebase/firestore';
import Sidebar from '../Shared/Sidebar';
import { useAuth } from '../../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';

export default function Projects() {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    targetAge: '',
    location: '',
    startDate: '',
    status: 'Active',
    focus: '',
    goal: '',
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  async function fetchProjects() {
    try {
      const snap = await getDocs(
        collection(db, 'projects')
      );
      setProjects(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
    } catch (err) {
      console.error(err);
    }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (editingId) {
        // setDoc + merge works whether the doc already exists (normal
        // edit) or not yet (first-ever edit of the hardcoded "Project
        // One" flagship project, which isn't a real Firestore doc until
        // someone edits it).
        await setDoc(doc(db, 'projects', editingId), {
          ...form,
          updatedAt: serverTimestamp(),
        }, { merge: true });
        setSuccess('Project updated successfully!');
      } else {
        // CREATE new project
        const projectId = `IYA-PRJ-${uuidv4().slice(0, 6).toUpperCase()}`;
        await addDoc(collection(db, 'projects'), {
          ...form,
          projectId,
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          studentsCount: 0,
          parentsCount: 0,
        });
        setSuccess(
          `Project created successfully! ID: ${projectId}`
        );
      }
      setShowForm(false);
      setEditingId(null);
      setForm({
        name: '', description: '', targetAge: '',
        location: '', startDate: '', status: 'Active',
        focus: '', goal: '',
      });
      fetchProjects();
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    }
    setLoading(false);
  }

  function handleEditDefaultProject() {
    setEditingId('default-project');
    setForm({
      name: 'Project One — Early Childhood Mentorship',
      description: 'Our flagship project helping students below Class 4 build character, gain life skills, and become future community leaders through weekly Saturday sessions, academic support, and family empowerment programs.',
      targetAge: 'Below Class 4',
      location: 'Tanzania',
      startDate: '2026',
      status: 'Active',
      focus: 'Character & Life Skills',
      goal: '',
    });
    setShowForm(true);
    setSuccess('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const hasCustomDefaultProject = projects.some(p => p.id === 'default-project');

  function handleEditClick(project) {
    setEditingId(project.id);
    setForm({
      name: project.name || '',
      description: project.description || '',
      targetAge: project.targetAge || '',
      location: project.location || '',
      startDate: project.startDate || '',
      status: project.status || 'Active',
      focus: project.focus || '',
      goal: project.goal || '',
    });
    setShowForm(true);
    setSuccess('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleCancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm({
      name: '', description: '', targetAge: '',
      location: '', startDate: '', status: 'Active',
      focus: '', goal: '',
    });
  }

  async function handleDelete(id) {
    if (window.confirm(
      'Are you sure you want to delete this project?'
    )) {
      await deleteDoc(doc(db, 'projects', id));
      fetchProjects();
    }
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Projects</div>
            <div style={styles.pageSub}>
              Manage all foundation projects
            </div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => (showForm ? handleCancelForm() : setShowForm(true))}
          >
            {showForm ? 'Cancel' : '+ New Project'}
          </button>
        </div>

        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}
        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        {/* DEFAULT PROJECT — shown only until it's been edited into a real Firestore doc */}
        {!hasCustomDefaultProject && (
        <div style={styles.defaultProject}>
          <div style={styles.defaultHeader}>
            <div style={styles.defaultIcon}></div>
            <div style={styles.defaultInfo}>
              <div style={styles.defaultName}>
                Project One — Early Childhood Mentorship
              </div>
              <div style={styles.defaultMeta}>
                IYA-PRJ-001 · Active since 2026 ·
                Classes 1-7
              </div>
            </div>
            <button style={styles.btnEdit} onClick={handleEditDefaultProject} title="Edit this project">
              Edit
            </button>
            <span style={styles.activeBadge}>
              Active
            </span>
          </div>
          <div style={styles.defaultDesc}>
            Our flagship project helping students below
            Class 4 build character, gain life skills,
            and become future community leaders through
            weekly Saturday sessions, academic support,
            and family empowerment programs.
          </div>
          <div style={styles.defaultStats}>
            <div style={styles.defaultStat}>
              <div style={styles.defaultStatVal}>
                
              </div>
              <div style={styles.defaultStatLabel}>
                Focus: Character & Life Skills
              </div>
            </div>
            <div style={styles.defaultStat}>
              <div style={styles.defaultStatVal}>
                
              </div>
              <div style={styles.defaultStatLabel}>
                Tanzania
              </div>
            </div>
            <div style={styles.defaultStat}>
              <div style={styles.defaultStatVal}>
                
              </div>
              <div style={styles.defaultStatLabel}>
                Ages 6-14
              </div>
            </div>
          </div>
        </div>
        )}

        {/* CREATE PROJECT FORM */}
        {showForm && (
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              {editingId ? 'Edit Project' : 'Create New Project'}
            </div>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Project Name *
                  </label>
                  <input
                    style={styles.formInput}
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g. Youth Leadership Program"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Location *
                  </label>
                  <input
                    style={styles.formInput}
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="e.g. Dar es Salaam"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Target Age Group
                  </label>
                  <input
                    style={styles.formInput}
                    name="targetAge"
                    value={form.targetAge}
                    onChange={handleChange}
                    placeholder="e.g. Ages 13-18"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Start Date
                  </label>
                  <input
                    style={styles.formInput}
                    name="startDate"
                    type="date"
                    value={form.startDate}
                    onChange={handleChange}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Project Focus
                  </label>
                  <input
                    style={styles.formInput}
                    name="focus"
                    value={form.focus}
                    onChange={handleChange}
                    placeholder="e.g. Leadership & Entrepreneurship"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Status
                  </label>
                  <select
                    style={styles.formInput}
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                  >
                    <option>Active</option>
                    <option>Planning</option>
                    <option>Completed</option>
                    <option>On Hold</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Project Goal
                  </label>
                  <input
                    style={styles.formInput}
                    name="goal"
                    value={form.goal}
                    onChange={handleChange}
                    placeholder="e.g. Empower 100 youth by 2027"
                  />
                </div>
                <div style={{
                  ...styles.formGroup,
                  gridColumn: '1 / -1'
                }}>
                  <label style={styles.formLabel}>
                    Description *
                  </label>
                  <textarea
                    style={{
                      ...styles.formInput,
                      height: '90px',
                      resize: 'vertical'
                    }}
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Describe this project..."
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                style={styles.submitBtn}
                disabled={loading}
              >
                {loading
                  ? (editingId ? 'Updating...' : 'Creating...')
                  : (editingId ? 'Update Project' : 'Create Project')}
              </button>
            </form>
          </div>
        )}

        {/* PROJECTS LIST */}
        {projects.length === 0 && !showForm ? (
          <div style={styles.panel}>
            <div style={styles.empty}>
              No additional projects yet. Create your
              next project above!
            </div>
          </div>
        ) : (
          projects.map(p => (
            <div key={p.id} style={styles.projectCard}>
              <div style={styles.projectHeader}>
                <div style={styles.projectIcon}>
                  {p.id === 'default-project' ? '' : ''}
                </div>
                <div style={styles.projectInfo}>
                  <div style={styles.projectName}>
                    {p.name}{p.id === 'default-project' ? ' · Flagship' : ''}
                  </div>
                  <div style={styles.projectMeta}>
                    {p.projectId || 'IYA-PRJ-001'} · {p.location} ·
                    {p.targetAge}
                  </div>
                </div>
                <span style={{
                  ...styles.statusBadge,
                  background:
                    p.status === 'Active' ? '#EAF3EC' :
                    p.status === 'Planning' ? '#FFF3E0' :
                    p.status === 'Completed' ? '#E3F2FD' :
                    '#F3F4F6',
                  color:
                    p.status === 'Active' ? '#14452F' :
                    p.status === 'Planning' ? '#E65100' :
                    p.status === 'Completed' ? '#1565C0' :
                    '#6B7280',
                }}>
                  {p.status}
                </span>
                <button
                  style={styles.btnEdit}
                  onClick={() => handleEditClick(p)}
                  title="Edit project"
                >
                  
                </button>
                <button
                  style={styles.btnDelete}
                  onClick={() => handleDelete(p.id)}
                  title="Delete project"
                >
                  Futa
                </button>
              </div>
              <div style={styles.projectDesc}>
                {p.description}
              </div>
              {p.focus && (
                <div style={styles.projectFocus}>
                  {p.focus}
                </div>
              )}
              {p.goal && (
                <div style={styles.projectGoal}>
                  Goal: {p.goal}
                </div>
              )}
            </div>
          ))
        )}

      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(245,124,0,0.25)',
  },
  successMsg: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  errorMsg: {
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    color: '#C62828',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  defaultProject: {
    background: 'linear-gradient(135deg, #0B3524, #175239)',
    borderRadius: '18px',
    padding: '24px',
    marginBottom: '20px',
    color: '#fff',
  },
  defaultHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },
  defaultIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    flexShrink: 0,
  },
  defaultInfo: { flex: 1 },
  defaultName: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '17px',
    marginBottom: '3px',
  },
  defaultMeta: {
    fontSize: '12px',
    opacity: 0.6,
  },
  activeBadge: {
    background: 'rgba(255,255,255,0.1)',
    color: '#BBD8C4',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
    border: '1px solid rgba(165,214,167,0.3)',
  },
  defaultDesc: {
    fontSize: '13px',
    opacity: 0.7,
    lineHeight: 1.6,
    marginBottom: '16px',
  },
  defaultStats: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },
  defaultStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  defaultStatVal: {
    fontSize: '16px',
  },
  defaultStatLabel: {
    fontSize: '12px',
    opacity: 0.6,
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#6B7280',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6',
    fontWeight: '600',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
    marginBottom: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  formLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500',
  },
  formInput: {
    padding: '11px 14px',
    borderRadius: '9px',
    border: '1.5px solid #E5E7EB',
    background: '#FAF9F5',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: '#1C1C1E',
    outline: 'none',
  },
  submitBtn: {
    width: '100%',
    padding: '13px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(20,69,47,0.25)',
  },
  empty: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '20px 0',
  },
  projectCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '14px',
  },
  projectHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  projectIcon: {
    width: '42px',
    height: '42px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #EAF3EC, #CFE3D4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    flexShrink: 0,
  },
  projectInfo: { flex: 1 },
  projectName: {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '3px',
  },
  projectMeta: {
    fontSize: '11.5px',
    color: '#6B7280',
  },
  statusBadge: {
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontWeight: '600',
  },
  btnDelete: {
    background: '#FFEBEE',
    color: '#C62828',
    border: 'none',
    padding: '7px 10px',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnEdit: {
    background: '#E3F2FD',
    color: '#1565C0',
    border: 'none',
    padding: '7px 10px',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  projectDesc: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: 1.6,
    marginBottom: '10px',
  },
  projectFocus: {
    fontSize: '12.5px',
    color: '#14452F',
    background: '#EAF3EC',
    padding: '6px 12px',
    borderRadius: '8px',
    display: 'inline-block',
    marginBottom: '6px',
    marginRight: '8px',
  },
  projectGoal: {
    fontSize: '12.5px',
    color: '#E65100',
    background: '#FFF3E0',
    padding: '6px 12px',
    borderRadius: '8px',
    display: 'inline-block',
  },
};